import * as React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Tooltip } from "@mui/material";
import {
  Navigate,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { MaterialSymbol } from "@datalake/towercontrol-banner";
import getUserProfiles from "../tokenUtils";

const USERS = [
  "vic-qa-admin",
  "vic-qa-user",
  "vic-prod-admin",
  "vic-prod-user",
];
const ADMINS = ["vic-qa-admin", "vic-prod-admin"];

// Sidebar navigation. `icon` is a Google Material Symbols Rounded name,
// `profiles` the Cognito groups allowed to see the entry.
export const MENU_ITEMS = [
  {
    title: "Dashboard",
    url: "/",
    icon: "grid_view",
    profiles: [...USERS, "vic-qa-no-access", "vic-prod-no-access"],
  },
  { title: "Materials", url: "/materials", icon: "inventory", profiles: USERS },
  {
    title: "Placeholder Materials",
    url: "/placeholdermaterials",
    icon: "data_object",
    profiles: USERS,
  },
  { title: "Systems", url: "/systems", icon: "wysiwyg", profiles: USERS },
  {
    title: "System Applications",
    url: "/systemapplications",
    icon: "device_hub",
    profiles: USERS,
  },
  { title: "Licenses", url: "/licenses", icon: "badge", profiles: USERS },
  { title: "Contracts", url: "/contracts", icon: "gavel", profiles: USERS },
  { title: "Orders", url: "/orders", icon: "storage", profiles: USERS },
  {
    title: "Serial Numbers",
    url: "/serialnumbers",
    icon: "data_array",
    profiles: USERS,
  },
  {
    title: "Data Quality Sap Orders",
    url: "/dataqualitysaporders",
    icon: "document_scanner",
    profiles: USERS,
  },
  {
    title: "Hardware Support Process",
    url: "/hwsprocess",
    icon: "device_unknown",
    profiles: USERS,
  },
  {
    title: "Missing SNs Status",
    url: "/missingsnsstatus",
    icon: "rule",
    profiles: USERS,
  },
  {
    title: "Athena Query",
    url: "/athena",
    icon: "manage_search",
    profiles: [...ADMINS, "vic-qa-dto", "vic-prod-dto"],
  },
  {
    title: "Client Errors",
    url: "/clienterrors",
    icon: "report_problem",
    profiles: ADMINS,
  },
  {
    title: "Unprocessed Items",
    url: "/unprocesseditems",
    icon: "find_in_page",
    profiles: ADMINS,
  },
  {
    title: "My Portal",
    url: "/myportal",
    icon: "web",
    profiles: ["vic-qa-my-portal", "vic-prod-my-portal"],
  },
  {
    title: "SFDC Synchronizer Monitoring",
    url: "/sfdcsynchronizermonitoring",
    icon: "sync",
    profiles: USERS,
  },
  {
    title: "Bulk Report",
    url: "/bulkreport",
    icon: "summarize",
    profiles: USERS,
  },
  {
    title: "Upload Document",
    url: "/upload",
    icon: "upload_file",
    profiles: ["vic-qa-admin", "vic-qa-user", "vic-prod-admin"],
  },
  {
    title: "Monitoring",
    url: "/monitoring",
    icon: "bar_chart",
    profiles: ADMINS,
  },
];

export default function MainListItems({ open = true, onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  let profiles = getUserProfiles();
  if (!profiles) {
    // No matching item found
    return <Navigate to="/fallback" replace />;
  }

  return (
    <React.Fragment>
      {MENU_ITEMS.filter((item) =>
        item.profiles.some((p) => profiles.includes(p)),
      ).map((item) => {
        // a section stays active on its detail pages
        const selected =
          item.url === "/"
            ? pathname === "/"
            : Boolean(matchPath({ path: item.url, end: false }, pathname));
        return (
          <Tooltip
            key={item.url}
            title={open ? "" : item.title}
            placement="right"
          >
            <ListItemButton
              selected={selected}
              aria-current={selected ? "page" : undefined}
              aria-label={open ? undefined : item.title}
              onClick={() => {
                navigate(item.url);
                onNavigate && onNavigate();
              }}
              sx={{
                minHeight: 40,
                mb: 0.5,
                px: open ? 1.5 : 0,
                justifyContent: open ? "flex-start" : "center",
                color: "grey.700",
              }}
            >
              <ListItemIcon
                sx={{ minWidth: open ? 36 : 0, justifyContent: "center" }}
              >
                <MaterialSymbol
                  name={item.icon}
                  size="sidebar"
                  filled={selected}
                />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: selected ? 600 : 500,
                    noWrap: true,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        );
      })}
    </React.Fragment>
  );
}
